import {
  DraftMessage,
  EContentSectionType,
  ESendRecipient,
  ESendTime,
  TFileSection,
  TImageSection,
  TMessageSection,
  TMessageSendOption,
  TVideoSection,
} from 'types';
import { getFileSize, getPosterFile, isValidUrl } from '../../utils';
import { MAX_LINE_IMAGE_SIZE, MAX_LINE_VIDEO_SIZE, MAX_PDF_FILE_SIZE, messageStrings, ONE_MB, sendErrorMessages } from '../../constants';
import { TCreateMessageForAll, TMedia } from '../../api/messages';
import { FormatSectionsOptions } from 'types';

const isSendRecipient = (value: unknown): value is ESendRecipient =>
  Object.values(ESendRecipient).includes(value as ESendRecipient);

const isSendTime = (value: unknown): value is ESendTime =>
  Object.values(ESendTime).includes(value as ESendTime);

/**
 * 保存済み下書きの旧形式を現在の送信対象へ正規化する。
 * 未知の値やFILTERなのにタグが無い値は、全員配信へ広げずnullで拒否する。
 */
export function normalizeDraftSendOption(
  sendOption: DraftMessage['sendOption'] | null | undefined,
): TMessageSendOption | null {
  if (
    !sendOption ||
    typeof sendOption !== 'object' ||
    Array.isArray(sendOption)
  ) {
    return null;
  }

  const tagIds = Array.isArray(sendOption.tagIds)
    ? [
        ...new Set(
          sendOption.tagIds.filter(
            (tagId): tagId is number =>
              Number.isInteger(tagId) && tagId > 0,
          ),
        ),
      ]
    : [];
  const hasTagIds = Object.prototype.hasOwnProperty.call(sendOption, 'tagIds');

  let recipient: ESendRecipient;
  if (sendOption.recipient === undefined) {
    if (tagIds.length > 0) {
      recipient = ESendRecipient.FILTER;
    } else if (hasTagIds) {
      return null;
    } else {
      recipient = ESendRecipient.ALL;
    }
  } else if (isSendRecipient(sendOption.recipient)) {
    recipient = sendOption.recipient;
  } else {
    return null;
  }

  if (recipient === ESendRecipient.FILTER && tagIds.length === 0) {
    return null;
  }

  let sendTime: ESendTime;
  if (sendOption.sendTime === undefined) {
    sendTime = ESendTime.IMMEDIATE;
  } else if (isSendTime(sendOption.sendTime)) {
    sendTime = sendOption.sendTime;
  } else {
    return null;
  }

  return {
    recipient,
    sendTime,
    ...(recipient === ESendRecipient.FILTER ? { tagIds } : {}),
    ...(typeof sendOption.scheduledDateTime === 'string'
      ? { scheduledDateTime: sendOption.scheduledDateTime }
      : {}),
  };
}

/**
 * Format sections for API (each section will be sent as separate message)
 * Uploads files to S3 and returns formatted sections with media URLs
 */
export async function formatSectionsForAPI(
  sections: TMessageSection[],
  options: FormatSectionsOptions
): Promise<TCreateMessageForAll[]> {
  const { uploadToS3, onError, maxVideoBytes = MAX_LINE_VIDEO_SIZE } = options;
  const hasInvalidAssetSection = sections.some(
    (section) =>
      (section.type === EContentSectionType.RICH_MESSAGE ||
        section.type === EContentSectionType.CARD_TYPE_MESSAGE) &&
      (!Number.isInteger(section.assetId) || (section.assetId ?? 0) <= 0),
  );

  if (hasInvalidAssetSection) {
    throw new Error(sendErrorMessages.invalidAssetSelection);
  }

  const hasInvalidSurveySection = sections.some(
    (section) =>
      section.type === EContentSectionType.SURVEY &&
      (!Number.isInteger(section.surveyId) || (section.surveyId ?? 0) <= 0),
  );

  if (hasInvalidSurveySection) {
    throw new Error(sendErrorMessages.surveySelectionRequired);
  }

  // 動画選択後に個別チャット送信をONにすると上限が変わるため、保存前にも検証する。
  // 超過動画だけを除外して本文のみ保存したり、他のファイルを先にアップロードしない。
  // 通知は呼び出し元の例外処理に任せる（onError も呼ぶと同じ文言が二重に出る）。
  if (
    sections.some((section) =>
      section.type === EContentSectionType.VIDEO &&
      section.file && section.file.size > maxVideoBytes,
    )
  ) {
    throw new Error(
      messageStrings.videoLimitSize.replace('$size', `${maxVideoBytes / ONE_MB}mb`),
    );
  }

  // 保存済み動画も新規ファイルのアップロードより先に検証する。
  // 例: 190MB上限で保存した挨拶を、個別チャット送信ON（100MB上限）へ切り替える。
  if (options.validateSavedVideos) {
    const savedVideoUrls = sections.flatMap((section) =>
      section.type === EContentSectionType.VIDEO &&
      !section.file && section.originalMedia?.url
        ? [section.originalMedia.url]
        : [],
    );
    if (savedVideoUrls.length > 0) {
      await options.validateSavedVideos(savedVideoUrls);
    }
  }

  const validateSizeOrToast = (
    file: File,
    maxBytes: number,
    // 種別に合ったエラー文言（省略時は従来どおり写真用）。'$size' が上限に置換される
    errorTemplate: string = sendErrorMessages.limitSize,
  ) => {
    if (file.size <= maxBytes) return true;

    const error = new Error(
      errorTemplate.replace(
        '$size',
        `${maxBytes / ONE_MB}mb`,
      ),
    );
    if (onError) {
      onError(error);
    }
    return false;
  };

  const validateUrlOrToast = (url: string): boolean => {
    const isValid = isValidUrl(url);

    if (!isValid) {
      if (onError) {
        onError(new Error(sendErrorMessages.invalidUrl));
      }
      return false;
    }

    return true;
  };

  const uploadSingle = async (file: File) => {
    const [collection] = await uploadToS3({ files: [file] });
    return collection;
  };

  const toTextMessage = (
    section: TMessageSection,
  ): TCreateMessageForAll | null => {
    if (section.type !== EContentSectionType.TEXT) return null;

    const text = section.content.trim();
    if (!text) return null;

    return {
      id: section.id,
      type: EContentSectionType.TEXT,
      content: section.content,
    };
  };

  const toImageMessage = async (
    section: TMessageSection,
  ): Promise<TCreateMessageForAll | null> => {
    if (section.type !== EContentSectionType.IMAGE) return null;

    const imageSection = section as TImageSection & { originalMedia?: TMedia };

    if (imageSection.file) {
      if (imageSection.showUrlInput && imageSection.actionUrl?.trim()) {
        if (!validateUrlOrToast(imageSection.actionUrl.trim())) return null;
      }
      if (!validateSizeOrToast(imageSection.file, MAX_LINE_IMAGE_SIZE))
        return null;

      const [sizeInfo, uploaded] = await Promise.all([
        getFileSize(imageSection.file),
        uploadSingle(imageSection.file),
      ]);

      if (!sizeInfo.originalHeight || !sizeInfo.originalWidth) {
        if (onError) {
          onError(new Error(sendErrorMessages.invalidMediaSize));
        }
        return null;
      }

      return {
        id: section.id,
        type: EContentSectionType.IMAGE,
        media: {
          url: uploaded.uploadedUrl,
          type: imageSection.file.type,
          actionUrl: imageSection.showUrlInput && imageSection.actionUrl?.trim() ? imageSection.actionUrl.trim() : undefined,
          fileName: uploaded.fileName,
          originalHeight: sizeInfo.originalHeight,
          originalWidth: sizeInfo.originalWidth,
        },
      };
    }

    if (imageSection.originalMedia && imageSection.originalMedia.url) {
      if (imageSection.showUrlInput && imageSection.actionUrl?.trim()) {
        if (!validateUrlOrToast(imageSection.actionUrl.trim())) return null;
      }

      return {
        id: section.id,
        type: EContentSectionType.IMAGE,
        media: {
          url: imageSection.originalMedia.url,
          type: imageSection.originalMedia.type,
          fileName: imageSection.originalMedia.fileName,
          originalHeight: imageSection.originalMedia.originalHeight,
          originalWidth: imageSection.originalMedia.originalWidth,
          actionUrl: imageSection.showUrlInput && imageSection.actionUrl?.trim() ? imageSection.actionUrl.trim() : undefined,
        },
      };
    }

    return null;
  };

  const toVideoMessage = async (
    section: TMessageSection,
  ): Promise<TCreateMessageForAll | null> => {
    if (section.type !== EContentSectionType.VIDEO) return null;

    const videoSection = section as TVideoSection & { originalMedia?: TMedia };

    if (videoSection.file) {
      if (videoSection.showUrlInput && videoSection.actionUrl?.trim()) {
        if (!validateUrlOrToast(videoSection.actionUrl.trim())) return null;
      }
      const objectUrl = URL.createObjectURL(videoSection.file);

      try {
        const [sizeInfo, posterFile] = await Promise.all([
          getFileSize(videoSection.file),
          getPosterFile(objectUrl, videoSection.file.type),
        ]);

        if (!sizeInfo.originalHeight || !sizeInfo.originalWidth) {
          if (onError) {
            onError(new Error(sendErrorMessages.invalidMediaSize));
          }
          return null;
        }

        const [videoUploaded, posterUploaded] = await Promise.all([
          uploadSingle(videoSection.file),
          uploadSingle(posterFile),
        ]);

        return {
          id: section.id,
          type: EContentSectionType.VIDEO,
          media: {
            url: videoUploaded.uploadedUrl,
            type: videoSection.file.type,
            fileName: videoUploaded.fileName,
            poster: posterUploaded.uploadedUrl,
            actionUrl: videoSection.showUrlInput && videoSection.actionUrl?.trim() ? videoSection.actionUrl.trim() : undefined,
            actionLabel: videoSection.showUrlInput && videoSection.actionLabel?.trim() ? videoSection.actionLabel.trim() : undefined,
            originalWidth: sizeInfo.originalWidth,
            originalHeight: sizeInfo.originalHeight,
          },
        };
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    }

    if (videoSection.originalMedia && videoSection.originalMedia.url) {
      if (videoSection.showUrlInput && videoSection.actionUrl?.trim()) {
        if (!validateUrlOrToast(videoSection.actionUrl.trim())) return null;
      }

      return {
        id: section.id,
        type: EContentSectionType.VIDEO,
        media: {
          url: videoSection.originalMedia.url,
          type: videoSection.originalMedia.type,
          fileName: videoSection.originalMedia.fileName,
          originalHeight: videoSection.originalMedia.originalHeight,
          originalWidth: videoSection.originalMedia.originalWidth,
          poster: videoSection.originalMedia.poster,
          actionUrl: videoSection.showUrlInput && videoSection.actionUrl?.trim() ? videoSection.actionUrl.trim() : undefined,
          actionLabel: videoSection.showUrlInput && videoSection.actionLabel?.trim() ? videoSection.actionLabel.trim() : undefined,
        },
      };
    }

    return null;
  };

  const toFileMessage = async (
    section: TMessageSection,
  ): Promise<TCreateMessageForAll | null> => {
    if (section.type !== EContentSectionType.FILE) return null;

    const fileSection = section;
    const caption = fileSection.content?.trim() || undefined;

    if (fileSection.file) {
      if (
        !validateSizeOrToast(
          fileSection.file,
          MAX_PDF_FILE_SIZE,
          messageStrings.pdfLimitSize,
        )
      )
        return null;

      const uploaded = await uploadSingle(fileSection.file);

      return {
        id: section.id,
        type: EContentSectionType.FILE,
        content: caption,
        media: {
          url: uploaded.uploadedUrl,
          type: fileSection.file.type,
          fileName: fileSection.file.name,
          originalHeight: 0,
          originalWidth: 0,
        },
      };
    }

    if (fileSection.originalMedia && fileSection.originalMedia.url) {
      return {
        id: section.id,
        type: EContentSectionType.FILE,
        content: caption,
        media: {
          url: fileSection.originalMedia.url,
          type: fileSection.originalMedia.type,
          fileName: fileSection.originalMedia.fileName,
          originalHeight: fileSection.originalMedia.originalHeight,
          originalWidth: fileSection.originalMedia.originalWidth,
        },
      };
    }

    return null;
  };

  const toAssetMessage = (
    section: TMessageSection,
  ): TCreateMessageForAll | null => {
    if (
      section.type !== EContentSectionType.RICH_MESSAGE &&
      section.type !== EContentSectionType.CARD_TYPE_MESSAGE
    ) {
      return null;
    }

    return {
      id: section.id,
      type: section.type,
      assetId: section.assetId as number,
    };
  };

  const toSurveyMessage = (
    section: TMessageSection,
  ): TCreateMessageForAll | null => {
    if (section.type !== EContentSectionType.SURVEY || !section.surveyId) {
      return null;
    }

    const imageUrl = section.imageUrl?.trim();
    const imageAspectRatio =
      section.imageAspectRatio === '1:1' || section.imageAspectRatio === '16:9'
        ? section.imageAspectRatio
        : imageUrl
          ? '16:9'
          : undefined;

    return {
      id: section.id,
      type: EContentSectionType.SURVEY,
      surveyId: section.surveyId,
      ...(section.content?.trim() ? { content: section.content.trim() } : {}),
      ...(imageUrl && imageAspectRatio
        ? { imageUrl, imageAspectRatio }
        : {}),
    };
  };

  const results = await Promise.all(
    sections.map(async (section) => {
      const text = toTextMessage(section);
      if (text) return text;

      const image = await toImageMessage(section);
      if (image) return image;

      const video = await toVideoMessage(section);
      if (video) return video;

      const file = await toFileMessage(section);
      if (file) return file;

      const asset = toAssetMessage(section);
      if (asset) return asset;

      const survey = toSurveyMessage(section);
      if (survey) return survey;

      return null;
    }),
  );

  return results.filter((x): x is TCreateMessageForAll => x !== null);
}

export function convertDraftSectionsToMessageSections(
  draftSections: TCreateMessageForAll[]
): TMessageSection[] {
  if (draftSections.length === 0) {
    return [{
      id: Date.now().toString(),
      type: EContentSectionType.TEXT,
      content: '',
    }];
  }

  return draftSections.map((section) => {
    if (section.type === EContentSectionType.TEXT) {
      return {
        id: section.id,
        type: EContentSectionType.TEXT,
        content: section.content || '',
      };
    } else if (section.type === EContentSectionType.IMAGE) {
      const messageSection: TImageSection & { originalMedia?: TMedia } = {
        id: section.id,
        type: EContentSectionType.IMAGE
      };

      if ('media' in section && section.media) {
        messageSection.actionUrl = section.media.actionUrl;
        messageSection.showUrlInput = !!section.media.actionUrl;
        messageSection.originalMedia = section.media;
      }

      return messageSection;
    } else if (section.type === EContentSectionType.VIDEO) {
      const messageSection: TVideoSection & { originalMedia?: TMedia } = {
        id: section.id,
        type: EContentSectionType.VIDEO
      };

      if ('media' in section && section.media) {
        messageSection.actionUrl = section.media.actionUrl;
        messageSection.actionLabel = section.media.actionLabel;
        messageSection.showUrlInput = !!section.media.actionUrl;
        messageSection.originalMedia = section.media;
      }

      return messageSection;
    } else if (section.type === EContentSectionType.FILE) {
      const messageSection: TFileSection = {
        id: section.id,
        type: EContentSectionType.FILE,
      };

      if ('content' in section && section.content) {
        messageSection.content = section.content;
      }
      if ('media' in section && section.media) {
        messageSection.originalMedia = section.media;
      }

      return messageSection;
    } else if (
      section.type === EContentSectionType.RICH_MESSAGE ||
      section.type === EContentSectionType.CARD_TYPE_MESSAGE
    ) {
      return {
        id: section.id,
        type: section.type,
        assetId: 'assetId' in section ? section.assetId : undefined,
      };
    } else if (section.type === EContentSectionType.SURVEY) {
      return {
        id: section.id,
        type: EContentSectionType.SURVEY,
        surveyId: 'surveyId' in section ? section.surveyId : undefined,
        content: 'content' in section ? section.content : undefined,
        imageUrl: 'imageUrl' in section ? section.imageUrl : undefined,
        imageAspectRatio:
          'imageAspectRatio' in section ? section.imageAspectRatio : undefined,
      };
    } else {
      return {
        id: section.id,
        type: EContentSectionType.TEXT,
        content:
          'content' in section && typeof section.content === 'string'
            ? section.content
            : '',
      };
    }
  });
}
